// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { cn } from '@/lib/utils';

// The unifying "group card with a table inside" pattern shared by /permissions
// (grouped by integration or access holder) and /integrations (one card per integration,
// its credentials inside). The header carries the group identity, a member
// count, and — when relevant — a pending badge; the body is whatever table or
// row list the surface supplies (docs/28 § /permissions, § /integrations).
export interface GroupCardProps {
  /** Group identity — typically a tile + name. */
  title: React.ReactNode;
  /** Optional metadata line shown under the title (kind · auth · delivery, etc.). */
  subtitle?: React.ReactNode;
  /** Member count shown in the header (grants / credentials). Omit to hide the count entirely. */
  count?: number;
  /** Singular noun for the count; pluralized with a trailing "s". Default "item". */
  countNoun?: string;
  /** When > 0, renders a pending badge in the header. */
  pendingCount?: number;
  /** Right-aligned header actions (e.g. "View grants"). */
  actions?: React.ReactNode;
  /** Tint the header with the muted surface, banding it off from the rows below
   * (mirrors the mobile group-header strip for a consistent grouped look). */
  mutedHeader?: boolean;
  /** Optional top-right header accessory, e.g. a StatusBadge. Rendered before count, pending badge, and actions. */
  statusAccessory?: React.ReactNode;
  /** The table / row list rendered inside the card. */
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function GroupCard({
  title,
  subtitle,
  count,
  countNoun = 'item',
  pendingCount = 0,
  actions,
  mutedHeader = false,
  statusAccessory,
  children,
  className,
  'data-testid': testId,
}: GroupCardProps): React.JSX.Element {
  return (
    <section
      data-testid={testId}
      className={cn('overflow-hidden rounded-gl-lg border border-border bg-card', className)}
    >
      <div
        className={cn(
          'flex flex-wrap items-start justify-between gap-x-4 gap-y-2 p-4 md:p-5',
          mutedHeader && 'bg-muted',
        )}
      >
        <div className="min-w-0 flex-1">
          {title}
          {subtitle && <div className="mt-1 min-w-0">{subtitle}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {statusAccessory}
          {count != null && (
            <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
              <span className="text-foreground">{count}</span> {countNoun}
              {count === 1 ? '' : 's'}
            </span>
          )}
          {pendingCount > 0 && <StatusBadge variant="pending">{pendingCount} pending</StatusBadge>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
      <div className="border-t border-border">{children}</div>
    </section>
  );
}
